<template>
    <vx-expandable-card>
        <template v-slot:header>Verified domains</template>
        <template v-slot:content>
            <div class="flex flex-col gap-4">
                <div class="text-sm text-slate-600">
                    Prove that your team owns an email domain by adding a DNS TXT record. Members whose email is in a
                    verified domain can log in with SSO. Each domain is verified on its own; subdomains need their own
                    record.
                </div>

                <form class="flex flex-col gap-2 sm:flex-row sm:items-start" @submit.prevent="addDomain">
                    <div class="grow">
                        <vx-input
                            v-model="form.domain"
                            size="lg"
                            placeholder="acme.com"
                            data-test="new-domain"
                            :error="displayFirstError(validation.domain.$errors)"
                        />
                    </div>
                    <vx-button type="submit" size="lg" color="primary" :loading="adding" data-test="add-domain">
                        Add domain <font-awesome-icon widthAuto :icon="faCirclePlus" />
                    </vx-button>
                </form>

                <page-loading v-if="loading" />

                <vx-alert v-else-if="loadFailed" color="danger" :closeable="false" data-test="load-failed">
                    <div class="flex items-center justify-between">
                        The domains could not be loaded.
                        <vx-button size="sm" color="danger" @click="loadDomains" data-test="retry-load">
                            Retry <font-awesome-icon widthAuto :icon="faRefresh" />
                        </vx-button>
                    </div>
                </vx-alert>

                <div v-else-if="!domains.length" class="text-sm text-slate-500" data-test="no-domains">
                    No domains yet.
                </div>

                <div
                    v-for="domain in domains"
                    :key="domain.id"
                    class="rounded-lg border border-slate-200 p-4 flex flex-col gap-3"
                    data-test="domain"
                >
                    <div class="flex flex-wrap items-center gap-2">
                        <span class="font-bold" data-test="domain-name">{{ domain.display_domain }}</span>
                        <span v-if="domain.display_domain !== domain.domain" class="text-xs text-slate-500">{{
                            domain.domain
                        }}</span>
                        <vx-badge
                            size="xs"
                            :color="domainStatusColor[domainStatusView(domain)]"
                            data-test="domain-status"
                        >
                            {{ domainStatusLabel[domainStatusView(domain)] }}
                        </vx-badge>
                        <span v-if="domain.last_checked_at" class="text-xs text-slate-500" data-test="last-checked">
                            Last checked {{ fromNow(domain.last_checked_at) }}
                        </span>
                    </div>

                    <vx-alert
                        v-if="domainStatusView(domain) === 'failing'"
                        color="danger"
                        :closeable="false"
                        data-test="grace-warning"
                    >
                        The TXT record is no longer found. Restore it within
                        {{ pluralized('day', graceDaysLeft(domain) ?? 0, true) }} or members with @{{
                            domain.display_domain
                        }}
                        emails will no longer be able to log in with SSO<template v-if="props.requireSso"
                            >, and because Require SSO is on they will be locked out</template
                        >.
                    </vx-alert>
                    <vx-alert
                        v-else-if="domainStatusView(domain) === 'unverified'"
                        color="danger"
                        :closeable="false"
                        data-test="unverified-warning"
                    >
                        Verification lapsed. Members with @{{ domain.display_domain }} emails cannot log in with SSO
                        until the record is restored and verified again.
                    </vx-alert>
                    <div
                        v-else-if="domain.last_check_result && domain.last_check_result !== 'found'"
                        class="text-sm text-amber-700"
                        data-test="check-hint"
                    >
                        {{ checkResultHint[domain.last_check_result] }}
                    </div>

                    <div class="grid gap-2 text-sm sm:grid-cols-[auto_1fr_auto] sm:items-center">
                        <span class="text-slate-500">Host</span>
                        <code class="break-all" data-test="txt-name">{{ domain.txt_record.name }}</code>
                        <vx-button
                            size="xs"
                            color="muted"
                            @click="copyText(domain.txt_record.name)"
                            data-test="copy-txt-name"
                            :aria-label="`Copy TXT record host: ${domain.txt_record.name}`"
                        >
                            Copy <font-awesome-icon widthAuto :icon="faCopy" />
                        </vx-button>
                        <span class="text-slate-500">Value</span>
                        <code class="break-all" data-test="txt-value">{{ domain.txt_record.value }}</code>
                        <vx-button
                            size="xs"
                            color="muted"
                            @click="copyText(domain.txt_record.value)"
                            data-test="copy-txt-value"
                            :aria-label="`Copy TXT record value: ${domain.txt_record.value}`"
                        >
                            Copy <font-awesome-icon widthAuto :icon="faCopy" />
                        </vx-button>
                    </div>
                    <div class="text-xs text-slate-500">
                        Some DNS providers want only <code>_voxie-verification</code> as the host.
                    </div>

                    <div class="flex gap-2">
                        <vx-button
                            size="sm"
                            color="muted-dark"
                            :loading="verifying === domain.id"
                            @click="verifyNow(domain)"
                            data-test="verify-now"
                        >
                            Verify now <font-awesome-icon widthAuto :icon="faArrowsRotate" />
                        </vx-button>
                        <vx-button
                            size="sm"
                            color="muted"
                            outline
                            @click="removeDomain(domain)"
                            data-test="remove-domain"
                        >
                            Remove <font-awesome-icon widthAuto :icon="faTrash" />
                        </vx-button>
                    </div>
                </div>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script lang="ts" setup>
import { faArrowsRotate, faCirclePlus, faCopy, faRefresh, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxBadge, VxButton, VxExpandableCard, VxInput, VxSwal, VxSwalError } from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import { onMounted, reactive, ref } from 'vue';
import { pluralized } from '~/components/filters';
import PageLoading from '~/components/general/PageLoading.vue';
import { useCopyText } from '~/composables/useCopyText';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import { useToasted } from '~/composables/useToasted';
import { ssoClient } from '~/services';
import type { SsoEmailDomain } from '~/services/ssoClient.types';
import { fromNow } from '~/utils/date';
import { catchRequestError, throwSwalRequestError } from '~/utils/request';
import { displayFirstError, validateRules } from '~/utils/validation';
import {
    checkResultHint,
    conflictText,
    domainStatusColor,
    domainStatusLabel,
    domainStatusView,
    graceDaysLeft,
} from './ssoSettingsHelpers';

const props = defineProps<{
    teamId: number;
    requireSso: boolean;
}>();

const emit = defineEmits<{
    changed: [];
}>();

const toasted = useToasted();
const { copyText } = useCopyText();
const { $externalResults, catchError } = useFormRequestHandler();

const domains = ref<SsoEmailDomain[]>([]);
const loading = ref(true);
const loadFailed = ref(false);
const adding = ref(false);
const verifying = ref<number | null>(null);

// the field is named after the API attribute so a 422 lands on it without remapping
const form = reactive({ domain: '' });
const validation = useVuelidate(
    {
        domain: {
            required,
            domain: helpers.withMessage(
                'Enter a domain such as acme.com, without a scheme, path or email address.',
                helpers.regex(/^[^\s@/:]+\.[^\s@/:]+$/)
            ),
        },
    },
    form,
    { $externalResults }
);

const loadDomains = async () => {
    loading.value = true;
    loadFailed.value = false;

    try {
        const { data } = await ssoClient.getEmailDomains(props.teamId);
        domains.value = data.data;
    } catch {
        loadFailed.value = true;
    } finally {
        loading.value = false;
    }
};

const addDomain = async () => {
    if (adding.value || !(await validateRules(validation))) {
        return;
    }

    adding.value = true;

    try {
        const { data } = await ssoClient.createEmailDomain(props.teamId, { domain: form.domain.trim() });
        domains.value = [...domains.value, data].sort((a, b) => a.domain.localeCompare(b.domain));
        form.domain = '';
        validation.value.$reset();
        toasted.success(`${data.display_domain} added. Add the TXT record, then verify.`);
        emit('changed');
    } catch (error) {
        catchError(error);
    } finally {
        adding.value = false;
    }
};

const replaceDomain = (updated: SsoEmailDomain) => {
    domains.value = domains.value.map((domain) => (domain.id === updated.id ? updated : domain));
};

const verifyNow = async (domain: SsoEmailDomain) => {
    verifying.value = domain.id;

    try {
        const { data } = await ssoClient.verifyEmailDomain(props.teamId, domain.id);
        replaceDomain(data);
        const found = data.last_check_result === 'found';
        toasted[found ? 'success' : 'error'](
            found ? `${data.display_domain} verified.` : `${data.display_domain} could not be verified.`
        );
        emit('changed');
    } catch (error) {
        toasted.error(catchRequestError(error));
    } finally {
        verifying.value = null;
    }
};

const removeDomain = (domain: SsoEmailDomain) => {
    VxSwal.confirmation(
        'Remove domain',
        `Members with @${domain.display_domain} emails will no longer be able to log in with SSO.`,
        'danger',
        () =>
            ssoClient
                .deleteEmailDomain(props.teamId, domain.id)
                .then(() => {
                    domains.value = domains.value.filter((candidate) => candidate.id !== domain.id);
                    emit('changed');
                })
                .catch((error) => {
                    const text = conflictText(error);

                    if (text !== null) {
                        throw new VxSwalError(text);
                    }

                    throwSwalRequestError(error);
                })
    );
};

onMounted(loadDomains);
</script>
